// Timetable Store - SAT V4 Zone
import { create } from 'zustand';
import { mockSimulationResults, mockTimetableVersions, mockTimetables } from '../lib/mockData';
import type {
  Conflict,
  SimulationResult,
  Timetable,
  TimetableEntry,
  TimetableFilters,
  TimetableVersion,
} from '../types';

interface TimetableState {
  // Data
  timetables: Timetable[];
  versions: TimetableVersion[];
  simulations: SimulationResult[];

  // Selection state
  currentTimetableId: string | null;
  activeTimetableId: string | null;

  // Generation state
  generationProgress: number;
  isGenerating: boolean;
  generationMessage: string;

  // Filters
  filters: TimetableFilters;

  // Actions - Timetables
  setCurrentTimetable: (id: string | null) => void;
  setActiveTimetable: (id: string | null) => void;
  createTimetable: (timetable: Timetable) => void;
  updateTimetable: (id: string, updates: Partial<Timetable>) => void;
  deleteTimetable: (id: string) => void;
  finalizeTimetable: (id: string) => void;

  // Actions - Entries
  addEntry: (timetableId: string, entry: TimetableEntry) => void;
  updateEntry: (timetableId: string, entryId: string, updates: Partial<TimetableEntry>) => void;
  deleteEntry: (timetableId: string, entryId: string) => void;
  lockEntry: (timetableId: string, entryId: string) => void;
  unlockEntry: (timetableId: string, entryId: string) => void;

  // Actions - Conflicts
  resolveConflict: (timetableId: string, conflictId: string) => void;
  ignoreConflict: (timetableId: string, conflictId: string) => void;

  // Actions - Generation (mock)
  startGeneration: () => void;
  stopGeneration: () => void;
  setGenerationProgress: (progress: number, message?: string) => void;

  // Actions - Filters
  setFilters: (filters: Partial<TimetableFilters>) => void;
  resetFilters: () => void;

  // Actions - Versions
  restoreVersion: (versionId: string) => void;

  // Getters
  getCurrentTimetable: () => Timetable | undefined;
  getActiveTimetable: () => Timetable | undefined;
  getUnresolvedConflicts: (timetableId: string) => Conflict[];
  getFilteredEntries: (timetableId: string) => TimetableEntry[];
}

const defaultFilters: TimetableFilters = {};

export const useTimetableStore = create<TimetableState>((set, get) => ({
  // Initialize with mock data
  timetables: mockTimetables,
  versions: mockTimetableVersions,
  simulations: mockSimulationResults,

  currentTimetableId: 'tt-main',
  activeTimetableId: 'tt-main',

  generationProgress: 0,
  isGenerating: false,
  generationMessage: '',

  filters: defaultFilters,

  // Timetable actions
  setCurrentTimetable: (id) => set({ currentTimetableId: id }),
  setActiveTimetable: (id) => set({ activeTimetableId: id }),

  createTimetable: (timetable) => set((state) => ({ timetables: [...state.timetables, timetable] })),

  updateTimetable: (id, updates) =>
    set((state) => ({
      timetables: state.timetables.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    })),

  deleteTimetable: (id) =>
    set((state) => ({
      timetables: state.timetables.filter((t) => t.id !== id),
    })),

  finalizeTimetable: (id) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === id ? { ...t, status: 'finalized' as const, updatedAt: new Date().toISOString() } : t,
      ),
      activeTimetableId: id,
    })),

  // Entry actions
  addEntry: (timetableId, entry) =>
    set((state) => ({
      timetables: state.timetables.map((t) => (t.id === timetableId ? { ...t, entries: [...t.entries, entry] } : t)),
    })),

  updateEntry: (timetableId, entryId, updates) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === timetableId
          ? {
              ...t,
              entries: t.entries.map((e) => (e.id === entryId ? { ...e, ...updates } : e)),
            }
          : t,
      ),
    })),

  deleteEntry: (timetableId, entryId) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === timetableId ? { ...t, entries: t.entries.filter((e) => e.id !== entryId) } : t,
      ),
    })),

  lockEntry: (timetableId, entryId) => get().updateEntry(timetableId, entryId, { isLocked: true }),

  unlockEntry: (timetableId, entryId) => get().updateEntry(timetableId, entryId, { isLocked: false }),

  // Conflict actions
  resolveConflict: (timetableId, conflictId) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === timetableId
          ? {
              ...t,
              conflicts: t.conflicts.map((c) => (c.id === conflictId ? { ...c, status: 'resolved' as const } : c)),
            }
          : t,
      ),
    })),

  ignoreConflict: (timetableId, conflictId) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === timetableId
          ? {
              ...t,
              conflicts: t.conflicts.map((c) => (c.id === conflictId ? { ...c, status: 'ignored' as const } : c)),
            }
          : t,
      ),
    })),

  // Generation actions (mock)
  startGeneration: () => {
    set({ isGenerating: true, generationProgress: 0, generationMessage: 'Starting generation...' });

    // Simulate generation progress
    const interval = setInterval(() => {
      const state = get();
      if (!state.isGenerating) {
        clearInterval(interval);
        return;
      }

      const newProgress = Math.min(state.generationProgress + Math.random() * 15, 100);
      const messages = [
        'Analyzing constraints...',
        'Resolving teacher allocations...',
        'Optimizing room assignments...',
        'Checking hard rules...',
        'Applying soft rules...',
        'Finalizing schedule...',
      ];
      const messageIndex = Math.floor((newProgress / 100) * messages.length);

      set({
        generationProgress: newProgress,
        generationMessage: messages[messageIndex] || 'Completing...',
      });

      if (newProgress >= 100) {
        clearInterval(interval);
        set({
          isGenerating: false,
          generationProgress: 100,
          generationMessage: 'Generation complete!',
        });
      }
    }, 500);
  },

  stopGeneration: () => set({ isGenerating: false }),

  setGenerationProgress: (progress, message) =>
    set({
      generationProgress: progress,
      generationMessage: message || '',
    }),

  // Filter actions
  setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),

  resetFilters: () => set({ filters: defaultFilters }),

  // Version actions
  restoreVersion: (versionId) => {
    const version = get().versions.find((v) => v.id === versionId);
    if (version) {
    }
  },

  // Getters
  getCurrentTimetable: () => {
    const state = get();
    return state.timetables.find((t) => t.id === state.currentTimetableId);
  },

  getActiveTimetable: () => {
    const state = get();
    return state.timetables.find((t) => t.id === state.activeTimetableId);
  },

  getUnresolvedConflicts: (timetableId) => {
    const timetable = get().timetables.find((t) => t.id === timetableId);
    return timetable?.conflicts.filter((c) => c.status === 'unresolved') || [];
  },

  getFilteredEntries: (timetableId) => {
    const state = get();
    const timetable = state.timetables.find((t) => t.id === timetableId);
    if (!timetable) return [];

    let entries = timetable.entries;
    const filters = state.filters;

    if (filters.subjectId) {
      entries = entries.filter((e) => e.subjectId === filters.subjectId);
    }
    if (filters.teacherId) {
      entries = entries.filter((e) => e.teacherId === filters.teacherId);
    }
    if (filters.roomId) {
      entries = entries.filter((e) => e.roomId === filters.roomId);
    }

    return entries;
  },
}));

export default useTimetableStore;
